import React from 'react';
import {
  ArrowLeft,
  PiggyBank,
  Save,
  Info,
  ExternalLink,
  Link2,
  Briefcase,
  X,
  Mail,
  TrendingUp
} from 'lucide-react';
import { BudgetCharts } from './BudgetCharts';
import { BudgetSummaryBox } from './BudgetSummaryBox';
import { BudgetSummary } from './BudgetSummary';
import { SaveBudgetModal } from './SaveBudgetModal';
import { sendEmail } from '../../lib/api';
import { formatBudgetForEmail } from '../../utils/budget';
import { useExpenseCalculations } from '../../hooks/useExpenseCalculations';
import { MINIMUM_WAGES } from '../../constants/minimum-wages';
import { useAuth } from '../../lib/auth-context';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { budgetCountries } from '../../data/budget-countries';
import { CityDataPage } from '../../pages/CityDataPage';


interface BudgetResultsProps {
  data: {
    initialSavings: string;
    pvtDuration: string;
    country: string;
    city: string;
    housingType: string;
    housingTypes: any[];
    groceriesCost: string;
    restaurantsCost: string;
    transportCost: string;
    phoneCost: string;
    internetCost: string;
    utilitiesCost: string;
    bankCost: string;
    streamingCost: string;
    leisureCost: string;
    epargne: string;
    expenseFields: { id: string; name: string; amount: string }[];
    flightCost: string;
    pvtCost: string;
    insuranceCost: string;
    firstLastMonthRent: string;
    furnitureCost: string;
    clothingCost: string;
    groceryCost: string;
    preDepartureExpenses?: { id: string; name: string; amount: string }[];
    setupExpenses?: { id: string; name: string; amount: string }[];
  };
  onBack: () => void;
  isPopup?: boolean;
  onClose?: () => void;
}

export function BudgetResults({ data, onBack, isPopup = false, onClose }: BudgetResultsProps) {
  const {
    monthlyExpenses,
    preDepartureCosts,
    setupCosts,
    totalBudget,
    monthlyBreakdown,
    preDepartureBreakdown,
    setupBreakdown
  } = useExpenseCalculations(data);
  const { user, userInfo } = useAuth();
  const [isSaving, setIsSaving] = React.useState(false);
  const [isSaved, setIsSaved] = React.useState(false);
  const [showSaveModal, setShowSaveModal] = React.useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = React.useState(false);
  const [showCityData, setShowCityData] = React.useState(false);
  const [showEmailModal, setShowEmailModal] = React.useState(false);
  const [isSendingEmail, setIsSendingEmail] = React.useState(false);

  // Show save modal when reaching results
  React.useEffect(() => {
    if (!isPopup && user && !isSaved) {
      const timer = setTimeout(() => {
        setShowSaveModal(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isPopup, user, isSaved]);

  // Format numbers with 2 decimal places
  const formatNumber = (num: number) =>
    num.toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const [showDetails, setShowDetails] = React.useState(false);
  const [includePreDeparture, setIncludePreDeparture] = React.useState(true);
  const [targetMonths, setTargetMonths] = React.useState(3);

  const handleMonthChange = (increment: boolean) => {
    setTargetMonths((prev) => {
      const newValue = increment ? prev + 1 : prev - 1;
      return Math.min(Math.max(newValue, 1), 24);
    });
  };

  const requiredSavings = React.useMemo(() => {
    return (
      (includePreDeparture ? preDepartureCosts : 0) +
      setupCosts +
      monthlyExpenses * targetMonths
    );
  }, [
    setupCosts,
    monthlyExpenses,
    targetMonths,
    includePreDeparture,
    preDepartureCosts,
  ]);

  const shortfall = totalBudget - Number(data.initialSavings);
  const hasSufficientFunds = Number(data.initialSavings) >= requiredSavings;

  // Calculer la durée de vie estimée avec les économies
  const survivalMonths = React.useMemo(() => {
    const totalSavings = Number(data.initialSavings);
    const monthlyBurn = monthlyExpenses;
    const setupCostsTotal = setupCosts + preDepartureCosts;

    if (monthlyBurn <= 0) return Infinity;

    const remainingAfterSetup = totalSavings - setupCostsTotal;
    if (remainingAfterSetup <= 0) return 0;

    return Math.floor(remainingAfterSetup / monthlyBurn);
  }, [data.initialSavings, monthlyExpenses, setupCosts, preDepartureCosts]);

  const getSurvivalMessage = () => {
    if (survivalMonths === Infinity)
      return 'Vos économies sont suffisantes pour couvrir vos dépenses indéfiniment !';
    if (survivalMonths <= 0)
      return 'Vos économies ne couvrent pas les coûts initiaux.';
    return `Vos économies vous permettent de survivre pendant ${survivalMonths} mois.`;
  };

  const handleSave = async () => {
    if (!user) return;

    setIsSaving(true);
    try {
      const budgetDoc = {
        userId: user.uid,
        country: data.country,
        city: data.city,
        monthlyExpenses,
        initialCosts: preDepartureCosts,
        preDepartureCosts,
        totalBudget,
        data: {
          ...data,
          housingTypes: data.housingTypes || [],
        },
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await addDoc(collection(db, 'budgets'), budgetDoc);
      
      // Send email after successful save
      if (userInfo?.user?.email) {
        await sendEmail(
          `Budget PVT ${data.city}`,
          formatBudgetForEmail({
            monthlyExpenses,
            preDepartureCosts,
            setupCosts,
            totalBudget,
            monthlyBreakdown,
            preDepartureBreakdown,
            setupBreakdown,
            data
          }),
          userInfo.user.email,
          userInfo
        );
      }
      
      setShowSaveModal(false);
      setShowSaveConfirmation(true);
      setIsSaved(true);
      
      // Fermer automatiquement après 3 secondes
      setTimeout(() => {
        setShowSaveConfirmation(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving budget:', error);
      // Show error modal
      const errorModal = document.createElement('div');
      errorModal.className = 'fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50';
      errorModal.innerHTML = `
        <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-premium max-w-md w-full p-6 transform animate-fade-in">
          <div class="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div class="relative">
              <div class="absolute inset-0 bg-gradient-to-br from-red-500/20 to-red-600/20 blur-xl rounded-full"></div>
              <div class="relative w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <svg class="w-8 h-8 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
          
          <div class="text-center mt-8">
            <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Erreur
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              Une erreur est survenue lors de la sauvegarde du budget. Veuillez réessayer.
            </p>
            
            <div class="mt-6 flex justify-center">
              <button onclick="this.closest('.fixed').remove()" class="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                Fermer
              </button>
            </div>
          </div>
        </div>
      `;
      document.body.appendChild(errorModal);
      
      // Auto-remove after 5 seconds
      setTimeout(() => {
        errorModal.remove();
      }, 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendEmail = async () => {
    if (!userInfo?.user?.email) return;
    
    setIsSendingEmail(true);
    try {
      await sendEmail(
        `Budget PVT ${data.city}`,
        formatBudgetForEmail({
          monthlyExpenses,
          preDepartureCosts,
          setupCosts,
          totalBudget,
          monthlyBreakdown,
          preDepartureBreakdown,
          setupBreakdown,
          data
        }),
        userInfo.user.email,
        userInfo
      );
      setShowEmailModal(false);
      // Show success confirmation modal
      const successModal = document.createElement('div');
      successModal.className = 'fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50';
      successModal.innerHTML = `
        <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-premium max-w-md w-full p-6 transform animate-fade-in">
          <div class="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div class="relative">
              <div class="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 blur-xl rounded-full"></div>
              <div class="relative w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                <svg class="w-8 h-8 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
          
          <div class="text-center mt-8">
            <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Budget envoyé !
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              Votre budget pour ${data.city} a été envoyé à ${userInfo.user.email}
            </p>
            
            <div class="mt-6 flex justify-center">
              <button onclick="this.closest('.fixed').remove()" class="px-4 py-2 text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors">
                Fermer
              </button>
            </div>
          </div>
        </div>
      `;
      document.body.appendChild(successModal);
      
      // Auto-remove after 3 seconds
      setTimeout(() => {
        successModal.remove();
      }, 3000);
    } catch (error) {
      console.error('Error sending email:', error);
      // Show error modal
      const errorModal = document.createElement('div');
      errorModal.className = 'fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50';
      errorModal.innerHTML = `
        <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-premium max-w-md w-full p-6 transform animate-fade-in">
          <div class="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div class="relative">
              <div class="absolute inset-0 bg-gradient-to-br from-red-500/20 to-red-600/20 blur-xl rounded-full"></div>
              <div class="relative w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <svg class="w-8 h-8 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
          
          <div class="text-center mt-8">
            <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Erreur d'envoi
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              Une erreur est survenue lors de l'envoi du budget. Veuillez réessayer.
            </p>
            
            <div class="mt-6 flex justify-center">
              <button onclick="this.closest('.fixed').remove()" class="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                Fermer
              </button>
            </div>
          </div>
        </div>
      `;
      document.body.appendChild(errorModal);
      
      // Auto-remove after 5 seconds
      setTimeout(() => {
        errorModal.remove();
      }, 5000);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const countryName =
    budgetCountries.find((c) => c.code === data.country)?.name ||
    'votre destination';

  // Helper function to convert hourly rate to EUR
  const getHourlyRateInEUR = (countryCode: string): number => {
    const wage = MINIMUM_WAGES[countryCode];
    if (!wage) return 10; // Fallback rate in EUR
    
    // Approximate conversion rates to EUR (these should ideally come from a real-time API)
    const conversionRates: { [key: string]: number } = {
      'AUD': 0.62,  // Australian Dollar
      'NZD': 0.58,  // New Zealand Dollar
      'CAD': 0.68,  // Canadian Dollar
      'JPY': 0.0063, // Japanese Yen
      'KRW': 0.00071, // South Korean Won
      'TWD': 0.030,  // Taiwan Dollar
      'ARS': 0.0010, // Argentine Peso
      'CLP': 0.00098, // Chilean Peso
      'COP': 0.00022, // Colombian Peso
      'MXN': 0.049,  // Mexican Peso
      'PEN': 0.25,   // Peruvian Sol
      'UYU': 0.024,  // Uruguayan Peso
      'BRL': 0.17,   // Brazilian Real
      'HKD': 0.12,   // Hong Kong Dollar
      'EUR': 1.0,    // Euro (base currency)
      'USD': 0.92    // US Dollar (for Ecuador)
    };
    
    const rate = conversionRates[wage.currency] || 1;
    return wage.rate * rate;
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 sm:gap-8">
      <div className={`${isPopup ? 'col-span-full' : 'xl:col-span-3'} relative`}>
        <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-premium border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />
            <div className="absolute inset-0 opacity-30">
              <div className="absolute top-0 right-0 w-72 h-72 bg-primary-500/20 rounded-full blur-3xl -translate-y-1/3 translate-x-1/4" />
              <div className="absolute bottom-0 left-0 w-56 h-56 bg-accent-500/20 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4" />
            </div>

            <div className="relative z-10 p-6 sm:p-8">
              <div className="flex justify-center mb-6">
                <img
                  src="https://pvtistes.net/wp-content/uploads/2025/01/rgb_pvtistes_logo_negative_white.png"
                  alt="Pvtistes Logo"
                  className="h-10 w-auto"
                />
              </div>

              <div className="text-center">
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 mb-4">
                  <PiggyBank className="w-4 h-4 text-accent-400" />
                  <span className="text-xs font-semibold text-accent-300 uppercase tracking-wider">
                    Résultat de la simulation
                  </span>
                </div>

                <h2 className="text-xl sm:text-3xl font-bold text-white mb-3">
                  Votre budget prévisionnel
                </h2>
                <p className="text-lg sm:text-xl text-gray-300">
                  {data.city && <span>{data.city}, </span>}
                  <span className="font-bold text-white">
                    {countryName}
                  </span>
                </p>
              </div>
            </div>
          </div>

          <div className="relative p-6">
            <div className="space-y-8">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl p-5 border border-emerald-100/50 dark:border-emerald-800/30">
                <div className="flex items-start gap-4">
                  <div className="p-2.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex-shrink-0">
                    <Briefcase className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-emerald-900 dark:text-emerald-200 mb-1.5">
                      À propos des revenus
                    </h3>
                    <p className="text-sm text-emerald-700 dark:text-emerald-300 leading-relaxed">
                      Ce budget estime vos dépenses, mais le PVT inclut souvent des périodes de
                      travail qui généreront des revenus. Votre capacité à subvenir à vos besoins
                      dépendra aussi de votre rythme de vie et des opportunités sur place.
                    </p>
                  </div>
                </div>
              </div>

              <BudgetSummary
                data={{
                  totalRequired: totalBudget,
                  currentSavings: Number(data.initialSavings),
                  monthlyExpenses,
                  initialCosts: preDepartureCosts,
                  setupCosts,
                  survivalMonths,
                  pvtDuration: data.pvtDuration,
                }}
              />

             

              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
                <div className="p-5 border-b border-gray-100 dark:border-gray-700">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-accent-500" />
                    Analyse Financière
                  </h3>
                </div>

                <div className="p-5 space-y-6">
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">
                      Scénarios de revenus
                    </h4>
                    <div className="space-y-2">
                      {[
                        { label: 'Minimal', desc: 'couvrir les bases', mult: 1, color: 'bg-amber-500' },
                        { label: 'Confortable', desc: 'vivre sereinement', mult: 1.2, color: 'bg-accent-500' },
                        { label: 'Optimal', desc: 'épargner et investir', mult: 1.5, color: 'bg-emerald-500' },
                      ].map((s) => (
                        <div key={s.label} className="flex items-center justify-between p-3.5 bg-gray-50 dark:bg-gray-700/30 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className={`w-2.5 h-2.5 rounded-full ${s.color}`} />
                            <div>
                              <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{s.label}</span>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{s.desc}</p>
                            </div>
                          </div>
                          <span className="text-base font-bold text-gray-900 dark:text-white tabular-nums">
                            {formatNumber(monthlyExpenses * s.mult)} €
                            <span className="text-xs font-normal text-gray-400">/mois</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-gray-100 dark:border-gray-700 pt-5">
                    <h4 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">
                      Temps de travail nécessaire
                    </h4>
                    <div className="bg-accent-50 dark:bg-accent-900/10 rounded-lg p-3 mb-3 border border-accent-100/50 dark:border-accent-800/30">
                      <p className="text-sm text-accent-700 dark:text-accent-300">
                        Basé sur le salaire minimum :{' '}
                        <span className="font-bold">
                          {MINIMUM_WAGES[data.country]?.rate.toLocaleString('fr-FR')}{' '}
                          {MINIMUM_WAGES[data.country]?.currency}/h
                        </span>
                      </p>
                    </div>

                    <div className="space-y-2">
                      {[
                        { label: 'Minimal', mult: 1 },
                        { label: 'Confortable', mult: 1.2 },
                        { label: 'Optimal', mult: 1.5 },
                      ].map((s) => (
                        <div key={s.label} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
                          <span className="text-sm text-gray-600 dark:text-gray-400">{s.label}</span>
                          <div className="text-right">
                            <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                              {Math.ceil((monthlyExpenses * s.mult) / getHourlyRateInEUR(data.country))} h/mois
                            </span>
                            <span className="text-xs text-gray-400 ml-2">
                              ({Math.ceil((monthlyExpenses * s.mult) / getHourlyRateInEUR(data.country) / 4)} h/sem)
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-start gap-2 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                    <Info className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Ces calculs sont basés sur le salaire minimum légal. Les taux réels
                      peuvent être plus élevés selon votre expérience et le secteur d'activité.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-8">
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
                  <div className="p-5 border-b border-gray-100 dark:border-gray-700">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                      Simulateur de durée
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Combien de temps souhaitez-vous vivre sans emploi ?
                    </p>
                  </div>

                  <div className="p-5 space-y-6">
                    <div className="flex items-center gap-2">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={includePreDeparture}
                          onChange={() => setIncludePreDeparture(!includePreDeparture)}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                      </label>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Inclure les dépenses avant le départ ({formatNumber(preDepartureCosts)} EUR)
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => handleMonthChange(false)}
                        disabled={targetMonths <= 1}
                        className="w-12 h-12 flex items-center justify-center bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition-all border border-gray-200 dark:border-gray-600"
                      >
                        <span className="text-xl font-bold text-gray-600 dark:text-gray-300">-</span>
                      </button>
                      <div className="flex-1 text-center py-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                        <div className="text-4xl sm:text-5xl font-black text-gray-900 dark:text-white tabular-nums">
                          {targetMonths}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium">mois</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleMonthChange(true)}
                        disabled={targetMonths >= 24}
                        className="w-12 h-12 flex items-center justify-center bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition-all border border-gray-200 dark:border-gray-600"
                      >
                        <span className="text-xl font-bold text-gray-600 dark:text-gray-300">+</span>
                      </button>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Budget nécessaire</span>
                        <span className="text-2xl font-black text-gray-900 dark:text-white tabular-nums">
                          {requiredSavings.toLocaleString('fr-FR')} EUR
                        </span>
                      </div>

                      <div className={`p-4 rounded-xl ${
                        hasSufficientFunds
                          ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/30'
                          : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/30'
                      }`}>
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            hasSufficientFunds
                              ? 'bg-emerald-100 dark:bg-emerald-900/40'
                              : 'bg-amber-100 dark:bg-amber-900/40'
                          }`}>
                            <span className="text-sm">{hasSufficientFunds ? '\u2713' : '!'}</span>
                          </div>
                          <div>
                            <p className={`text-sm font-semibold ${
                              hasSufficientFunds ? 'text-emerald-800 dark:text-emerald-300' : 'text-amber-800 dark:text-amber-300'
                            }`}>
                              {hasSufficientFunds
                                ? 'Votre budget couvre cette durée'
                                : `Il vous manque ${Math.abs(Number(data.initialSavings) - requiredSavings).toLocaleString('fr-FR')} EUR`}
                            </p>
                            <p className={`text-xs mt-1 ${
                              hasSufficientFunds ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'
                            }`}>
                              {hasSufficientFunds
                                ? "Vous avez suffisamment d'économies pour cette période"
                                : 'Continuez à épargner ou réduisez la durée prévue sans emploi'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                      <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                        Ce montant comprend vos coûts d'installation ({formatNumber(setupCosts)} EUR),
                        {includePreDeparture && ` vos dépenses avant le départ (${formatNumber(preDepartureCosts)} EUR),`}
                        {' '}vos dépenses mensuelles ({formatNumber(monthlyExpenses)} EUR/mois) pour {targetMonths} mois.
                      </p>
                      <button
                        onClick={() => setShowCityData(true)}
                        className="inline-flex items-center gap-2 mt-3 px-4 py-2.5 bg-gray-50 dark:bg-gray-700/30 hover:bg-gray-100 dark:hover:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl transition-all duration-200 group"
                      >
                        <Link2 className="w-4 h-4 text-primary-500" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Voir les prix détaillés pour {data.city}
                        </span>
                        <ExternalLink className="w-3.5 h-3.5 text-gray-400 transition-transform group-hover:translate-x-0.5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Graphiques */}
                <BudgetCharts
                  data={{
                    monthlyExpenses: monthlyBreakdown,
                    preDepartureCosts: preDepartureBreakdown,
                    setupCosts: setupBreakdown
                  }}
                />
              </div>

              {/* Ajout du bloc de concours promotionnel */}

              <div className="flex flex-col sm:flex-row justify-between gap-4 mt-12 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full">
                  <button
                    onClick={() => setShowEmailModal(true)}
                    className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors border border-gray-200 dark:border-gray-700 w-full sm:w-auto"
                  >
                    <Mail className="w-5 h-5" />
                    <span>Envoyer par email</span>
                  </button>
                  
                  {!isPopup && user && (
                    <button
                      onClick={() => setShowSaveModal(true)}
                      disabled={isSaving || isSaved}
                      className={`flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 text-white rounded-xl transition-colors w-full sm:w-auto ${
                        isSaved
                          ? 'bg-emerald-500 cursor-not-allowed'
                          : 'bg-primary-600 hover:bg-primary-700'
                      }`}
                    >
                      <Save className="w-5 h-5" />
                      <span>
                        {isSaved ? 'Budget sauvegardé' : 'Sauvegarder le budget'}
                      </span>
                    </button>
                  )}
                  
                  {isPopup && (
                    <button
                      onClick={onClose}
                      className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors border border-gray-200 dark:border-gray-700 w-full sm:w-auto"
                    >
                      <X className="w-5 h-5" />
                      <span>Fermer</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {!isPopup && (
        <div className="hidden xl:block xl:col-span-2">
          <div className="sticky top-[8.5rem] pt-8">
            <BudgetSummaryBox
              formData={data}
              housingTypes={data.housingTypes}
              currency="EUR"
              city={data.city}
              countryName={countryName}
              currentStep={5}
            />
          </div>
        </div>
      )}

      {/* Save Budget Modal */}
      <SaveBudgetModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSave}
        isSaving={isSaving}
        willSendEmail={true}
      />

      {/* Save Confirmation Modal */}
      {showSaveConfirmation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-premium max-w-md w-full p-6 transform animate-fade-in">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 blur-xl rounded-full"></div>
                <div className="relative w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                  <Save className="w-8 h-8 text-emerald-500" />
                </div>
              </div>
            </div>
            
            <div className="text-center mt-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Budget sauvegardé !
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Votre budget pour {data.city} a été enregistré avec succès.
              </p>
              
              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => setShowSaveConfirmation(false)}
                  className="px-4 py-2 text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-premium w-full max-w-md transform animate-fade-in">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary-100/20 to-accent-100/20 dark:from-primary-800/10 dark:to-accent-800/10 rounded-full transform translate-x-32 -translate-y-32 blur-3xl pointer-events-none" />
            
            <div className="relative">
            <div className="p-4 border-b dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                  <Mail className="w-5 h-5 text-primary-500 dark:text-primary-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Envoyer le budget par email
                </h3>
              </div>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Adresse email
                </label>
                <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200/50 dark:border-gray-700/50">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    {userInfo?.user?.email || ''}
                  </span>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSendEmail}
                  disabled={!userInfo?.user?.email || isSendingEmail}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white text-sm font-medium rounded-lg transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-sm hover:shadow-md"
                >
                  {isSendingEmail ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Envoi...</span>
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      <span>Envoyer</span>
                    </>
                  )}
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* City Data Modal */}
      {showCityData && (
        <CityDataPage
          isOpen={true}
          onClose={() => setShowCityData(false)}
          modalCountryCode={data.country}
          modalCity={data.city}
        />
      )}
    </div>
  );
}